import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../../i18n';
import { formatMontant, getDeviseCourante } from '../../utils/format';
import {
  ShoppingCart, Users, Globe, Wallet, TrendingUp,
  AlertTriangle, Package, CheckCircle, Lock, X,
  Clock, ArrowUpRight, ArrowDownRight, RefreshCw, SkipForward
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PeriodPills, { getPeriodRange } from '../../components/common/PeriodPills';
import CountUp from '../../components/common/CountUp';
import EvolutionChart from './EvolutionChart';
import TopProduitsChart from './TopProduitsChart';

function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}

const StatCard = ({ icon: Icon, label, value, color = 'yellow', subtitle, onClick }) => {
  const colors = {
    yellow: 'from-yellow-600/20 to-yellow-500/5 border-yellow-600/30 text-yellow-400',
    green:  'from-green-600/20 to-green-500/5 border-green-600/30 text-green-400',
    blue:   'from-blue-600/20 to-blue-500/5 border-blue-600/30 text-blue-400',
    red:    'from-red-600/20 to-red-500/5 border-red-600/30 text-red-400',
    orange: 'from-orange-600/20 to-orange-500/5 border-orange-600/30 text-orange-400',
  };
  return (
    <div
      onClick={onClick}
      className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5 ${onClick ? 'cursor-pointer hover:brightness-110 transition-all' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="p-2.5 rounded-lg bg-white/5">
          <Icon size={22} className={colors[color].split(' ')[3]} />
        </div>
      </div>
    </div>
  );
};

// ── Modal confirmation clôture ────────────────────────────────────────────────
function ModalCloture({ stats, onClose, onSuccess }) {
  const { moduleActif } = useAuth();
  const aTerrainM = moduleActif('vendeurs_terrain');
  const aLigneM   = moduleActif('commandes_ligne');
  const [loading, setLoading]   = useState(false);
  const [reporting, setReporting] = useState(false);
  const sortiesOuvertes      = stats?.sortiesOuvertes || [];
  const nbSortiesOuvertes    = sortiesOuvertes.length;
  const valeurOuvertes       = Number(stats?.valeurSortiesOuvertes || 0);
  const aSortiesOuvertes     = nbSortiesOuvertes > 0;

  async function confirmer() {
    setLoading(true);
    try {
      await api.post('/dashboard/cloture');
      toast.success('Journée clôturée avec succès !');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la clôture');
    } finally {
      setLoading(false);
    }
  }

  // Reporte toutes les sorties ouvertes du jour au lendemain
  // (chacune devient une nouvelle tournée sur J+1, l'ancienne passe en 'reportee')
  async function reporterAuLendemain() {
    setReporting(true);
    try {
      const { data } = await api.post('/vendeurs/sorties/reporter');
      toast.success(data.message || `${data.count} sortie(s) reportée(s)`, { duration: 4000 });
      onSuccess(); // referme + recharge le dashboard
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du report');
    } finally {
      setReporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Lock size={18} className="text-yellow-400" /> Clôturer la journée
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="bg-orange-900/20 border border-orange-500/30 rounded-xl p-4 mb-5">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
            <p className="text-orange-300 text-sm">
              Cette action est <strong>irréversible</strong>. Elle verrouille toutes les données du jour et génère un snapshot définitif.
            </p>
          </div>
        </div>

        {/* ⚠️ Avertissement : sorties vendeur encore ouvertes */}
        {aSortiesOuvertes && (
          <div className="bg-red-900/30 border border-red-700/60 rounded-xl p-4 mb-5">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-300 font-bold text-sm">
                  {nbSortiesOuvertes} sortie{nbSortiesOuvertes > 1 ? 's' : ''} vendeur non régularisée{nbSortiesOuvertes > 1 ? 's' : ''}
                </p>
                <p className="text-red-200/80 text-xs mt-1">
                  Valeur en jeu : <strong>{formatGNF(valeurOuvertes)}</strong> · ce montant
                  ne sera <strong>pas</strong> compté dans la clôture si tu valides maintenant.
                  Le retour du vendeur sera alors enregistré à la date où il rentrera.
                </p>
              </div>
            </div>
            <ul className="space-y-1 ml-7">
              {sortiesOuvertes.map(s => (
                <li key={s.id} className="text-xs text-red-200/90 flex items-center justify-between gap-2">
                  <span className="truncate">
                    {s.vendeur}
                    {s.numero_tournee > 1 && <span className="text-red-300/60"> · Tournée #{s.numero_tournee}</span>}
                    <span className="text-red-300/50"> ({s.pieces_confiees} pcs)</span>
                  </span>
                  <span className="text-red-100 font-medium whitespace-nowrap">{formatGNF(s.valeur_sortie)}</span>
                </li>
              ))}
            </ul>
            <p className="text-red-200/70 text-[11px] mt-3 italic">
              Recommandation : termine d'abord les retours du soir via la page Vendeurs,
              ou reporte ces tournées au lendemain (option ci-dessous).
            </p>

            {/* Bouton « Reporter au lendemain » — alternative à la clôture forcée */}
            <button
              onClick={reporterAuLendemain}
              disabled={reporting || loading}
              className="w-full mt-3 py-2.5 bg-blue-700/30 hover:bg-blue-700/50 disabled:opacity-40 text-blue-300 font-semibold rounded-lg flex items-center justify-center gap-2 transition-all border border-blue-700/50"
              title="Reporte toutes les sorties ouvertes comme premières tournées de demain"
            >
              {reporting
                ? <div className="w-4 h-4 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
                : <><SkipForward size={14} /> Reporter au lendemain ({nbSortiesOuvertes})</>}
            </button>
          </div>
        )}

        <div className="space-y-2 mb-6 text-sm">
          <div className="flex justify-between py-1.5 border-b border-gray-800">
            <span className="text-gray-400">Ventes magasin</span>
            <span className="text-white font-medium">{formatGNF(stats?.ventesMagasin)}</span>
          </div>
          {aTerrainM && (
            <div className="flex justify-between py-1.5 border-b border-gray-800">
              <span className="text-gray-400">Ventes terrain</span>
              <span className="text-white font-medium">{formatGNF(stats?.ventesVendeurs)}</span>
            </div>
          )}
          {aLigneM && (
            <div className="flex justify-between py-1.5 border-b border-gray-800">
              <span className="text-gray-400">Vente en Ligne</span>
              <span className="text-white font-medium">{formatGNF(stats?.ventesLivraison)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 font-bold text-base">
            <span className="text-gray-200">Total encaissé</span>
            <span className="text-yellow-400">{formatGNF(stats?.totalEncaisse)}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-xl transition-all">
            Annuler
          </button>
          <button onClick={confirmer} disabled={loading}
            className={`flex-1 py-3 disabled:opacity-40 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all ${
              aSortiesOuvertes ? 'bg-red-700 hover:bg-red-600' : 'bg-yellow-600 hover:bg-yellow-500 text-black'
            }`}
            title={aSortiesOuvertes ? 'Clôture forcée malgré sorties ouvertes' : 'Clôture standard'}
          >
            {loading
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <><Lock size={16} /> {aSortiesOuvertes ? 'Clôturer quand même' : 'Clôturer'}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Activité récente ──────────────────────────────────────────────────────────
function ActiviteItem({ op }) {
  const isEntree = op.type === 'entree';
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-800/60 last:border-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isEntree ? 'bg-green-500/10' : 'bg-red-500/10'
      }`}>
        {isEntree
          ? <ArrowUpRight size={14} className="text-green-400" />
          : <ArrowDownRight size={14} className="text-red-400" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-200 text-sm truncate">{op.description}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-gray-600 text-xs">{op.canal}</span>
          <span className="text-gray-700 text-xs">·</span>
          <Clock size={10} className="text-gray-600" />
          <span className="text-gray-600 text-xs">
            {op.created_at ? new Date(op.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
          </span>
        </div>
      </div>
      <span className={`text-sm font-bold whitespace-nowrap ${isEntree ? 'text-green-400' : 'text-red-400'}`}>
        {isEntree ? '+' : '-'}{formatGNF(op.montant)}
      </span>
    </div>
  );
}

// ── Page Dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [modalCloture, setModalCloture] = useState(false);
  const { isProprietaire, moduleActif } = useAuth();
  // Tableau de bord dynamique : les cartes liées à un module désactivé
  // (par le Super Admin) sont masquées et les grilles se réadaptent.
  const aTerrain = moduleActif('vendeurs_terrain');
  const aLigne   = moduleActif('commandes_ligne');
  const colsClass = (n) => ({
    1: 'grid grid-cols-1 gap-4',
    2: 'grid grid-cols-2 gap-4',
    3: 'grid grid-cols-2 lg:grid-cols-3 gap-4',
    4: 'grid grid-cols-2 lg:grid-cols-4 gap-4',
    5: 'grid grid-cols-2 lg:grid-cols-5 gap-4',
  }[Math.max(1, Math.min(5, n))]);

  // Filtre période — pilote les KPI financiers (CA par canal + bénéfice net).
  // Les KPI opérationnels (commandes attente, ruptures, vendeurs actifs,
  // sorties ouvertes) restent en temps réel et ne suivent PAS le filtre.
  const [period, setPeriod]         = useState(() => getPeriodRange('today'));
  const [periodStats, setPeriodStats] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/dashboard/today');
      setStats(data);
    } catch {
      setStats({
        ventesMagasin: 0, nbVentesMagasin: 0,
        ventesVendeurs: 0, nbSortiesVendeurs: 0,
        ventesLivraison: 0, nbLivraisons: 0,
        totalEncaisse: 0, beneficeBrut: 0,
        articlesVendus: 0, commandesEnAttente: 0,
        ecartsVendeurs: 0, rupturesStock: 0, alertesStock: 0,
        vendeurActifs: 0, activiteRecente: [],
      });
      toast.error('Impossible de charger les données du dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const chargerPeriode = useCallback(async (p) => {
    setPeriodLoading(true);
    try {
      const { data } = await api.get('/dashboard/periode', {
        params: { from: p.from, to: p.to },
      });
      setPeriodStats(data);
    } catch {
      setPeriodStats(null);
      toast.error('Impossible de charger les données de la période');
    } finally {
      setPeriodLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { chargerPeriode(period); }, [period, chargerPeriode]);

  // Le bouton Actualiser recharge ET réinitialise le filtre période à
  // « Aujourd'hui ». Comme setPeriod reçoit un nouvel objet, le useEffect
  // [period] redéclenche automatiquement chargerPeriode — pas besoin de
  // l'appeler ici, sinon on aurait un double fetch.
  const refreshAll = useCallback(() => {
    charger();
    setPeriod(getPeriodRange('today'));
  }, [charger]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-white">{t('dashboard.titre')}</h1>
          <p className="text-gray-400 text-sm capitalize mt-0.5">{today}</p>
          {/* Filtre période — pilote les KPI financiers uniquement */}
          <div className="mt-3">
            <PeriodPills value={period.key} onChange={setPeriod} />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={refreshAll}
            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-xl transition-all" title="Actualiser">
            <RefreshCw size={16} />
          </button>
          {/* Clôture journée : action sensible, on la cache aux employés.
              Côté backend, /api/dashboard/cloture est déjà writeOwner. */}
          {isProprietaire && (
            <button onClick={() => setModalCloture(true)}
              className="flex items-center gap-2 bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-bold transition-colors">
              <Lock size={14} /> {t('dashboard.clotureJournee')}
            </button>
          )}
        </div>
      </div>

      {/* Ruptures de stock — critique */}
      {stats?.rupturesStock > 0 && (
        <div className="bg-red-900/30 border border-red-700/50 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
          <p className="text-red-300 text-sm">
            <strong>{stats.rupturesStock} article{stats.rupturesStock > 1 ? 's' : ''} en rupture de stock</strong> — stock à zéro, réapprovisionnement urgent
          </p>
        </div>
      )}

      {/* Alertes stock — avertissement */}
      {stats?.alertesStock > 0 && (
        <div className="bg-orange-900/30 border border-orange-700/50 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={20} className="text-orange-400 flex-shrink-0" />
          <p className="text-orange-300 text-sm">
            <strong>{stats.alertesStock} article{stats.alertesStock > 1 ? 's' : ''}</strong> en alerte de stock — quantité proche du seuil minimum
          </p>
        </div>
      )}

      {/* Bandeau période active — pour rappeler ce que les KPI financiers couvrent */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="text-gray-400">Période :</span>
        <span className="text-yellow-400 font-medium">{period.label}</span>
        {periodLoading && (
          <span className="animate-spin w-3 h-3 border-2 border-yellow-500 border-t-transparent rounded-full ml-1" />
        )}
      </div>

      {/* KPI CA par canal — chiffres animés, pilotés par le filtre période.
          Les canaux des modules désactivés sont masqués. */}
      <div className={colsClass(2 + (aTerrain ? 1 : 0) + (aLigne ? 1 : 0))}>
        <StatCard
          icon={ShoppingCart} label={t('dashboard.ventesMagasin')} color="yellow"
          value={<CountUp value={periodStats?.ventesMagasin} formatter={formatGNF} />}
          subtitle={`${periodStats?.nbVentesMagasin || 0} vente${periodStats?.nbVentesMagasin > 1 ? 's' : ''}`}
        />
        {aTerrain && (
          <StatCard
            icon={Users} label={t('dashboard.ventesVendeurs')} color="blue"
            value={<CountUp value={periodStats?.ventesVendeurs} formatter={formatGNF} />}
            subtitle={`${periodStats?.nbSortiesVendeurs || 0} sortie${periodStats?.nbSortiesVendeurs > 1 ? 's' : ''}`}
          />
        )}
        {aLigne && (
          <StatCard
            icon={Globe} label={t('dashboard.ventesLivraison')} color="green"
            value={<CountUp value={periodStats?.ventesLivraison} formatter={formatGNF} />}
            subtitle={`${periodStats?.nbLivraisons || 0} vente${periodStats?.nbLivraisons > 1 ? 's' : ''}`}
          />
        )}
        <StatCard
          icon={Wallet} label={t('dashboard.totalEncaisse')} color="yellow"
          value={<CountUp value={periodStats?.chiffreAffaires} formatter={formatGNF} />}
        />
      </div>

      {/* Bénéfice net — réservé propriétaire (et admin lecture).
          Mêmes règles compta que /api/rapports/benefice, sur la période. */}
      {isProprietaire && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <StatCard
            icon={TrendingUp}
            label={`Bénéfice net — ${period.label.toLowerCase()}`}
            color={Number(periodStats?.beneficeNet || 0) >= 0 ? 'green' : 'red'}
            value={<CountUp value={periodStats?.beneficeNet} formatter={formatGNF} />}
            subtitle="Ventes − COGS − dépenses opé"
          />
          <StatCard
            icon={ShoppingCart} label="Coût des marchandises (COGS)" color="orange"
            value={<CountUp value={periodStats?.cogs} formatter={formatGNF} />}
            subtitle="coût d'achat des produits vendus"
          />
          <StatCard
            icon={ArrowDownRight} label="Dépenses opérationnelles" color="red"
            value={<CountUp value={periodStats?.depensesOperationnelles} formatter={formatGNF} />}
            subtitle="transport, salaire, etc."
          />
        </div>
      )}

      {/* KPI secondaires — réadaptés selon les modules actifs */}
      <div className={colsClass(2 + (aLigne ? 1 : 0) + (aTerrain ? 2 : 0))}>
        <StatCard
          icon={TrendingUp} label="Articles vendus" color="blue"
          value={stats?.articlesVendus || 0}
          subtitle="tous canaux"
        />
        {aLigne && (
          <StatCard
            icon={Package} label={t('dashboard.commandesEnAttente')} color="orange"
            value={stats?.commandesEnAttente || 0}
            subtitle="en cours"
          />
        )}
        {aTerrain && (
          <StatCard
            icon={AlertTriangle} label={t('dashboard.ecartsVendeurs')} color="red"
            value={formatGNF(stats?.ecartsVendeurs)}
            subtitle="dettes terrain"
          />
        )}
        <StatCard
          icon={Package} label="Ruptures stock" color="red"
          value={stats?.rupturesStock || 0}
          subtitle={stats?.alertesStock > 0 ? `+ ${stats.alertesStock} en alerte` : 'aucune alerte'}
        />
        {aTerrain && (
          <StatCard
            icon={Users} label={t('dashboard.vendeurActifs')} color="green"
            value={stats?.vendeurActifs || 0}
            subtitle="aujourd'hui"
          />
        )}
      </div>

      {/* Graphiques — suivent la période sélectionnée dans PeriodPills */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <EvolutionChart period={period} />
        </div>
        <TopProduitsChart period={period} limit={5} />
      </div>

      {/* Résumé canaux + activité */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Répartition CA — suit la période sélectionnée */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-yellow-400" />
            Répartition du CA — {period.label.toLowerCase()}
          </h3>
          {(periodStats?.chiffreAffaires || 0) === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">Aucune vente sur la période</p>
          ) : (
            <div className="space-y-3">
              {[
                { label: 'Magasin',       val: periodStats?.ventesMagasin,   color: 'bg-yellow-500', visible: true },
                { label: 'Terrain',       val: periodStats?.ventesVendeurs,  color: 'bg-blue-500',   visible: aTerrain },
                { label: 'Vente en Ligne',val: periodStats?.ventesLivraison, color: 'bg-green-500',  visible: aLigne },
              ].filter(i => i.visible).map(item => {
                const ca  = periodStats?.chiffreAffaires || 0;
                const pct = ca > 0 ? Math.round(((item.val || 0) / ca) * 100) : 0;
                return (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-400">{item.label}</span>
                      <span className="text-white font-medium">{formatGNF(item.val)} <span className="text-gray-500 text-xs">({pct}%)</span></span>
                    </div>
                    <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Activité récente — reste en temps réel (8 dernières opérations
            de la journée en cours), indépendant du filtre période. */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <CheckCircle size={16} className="text-green-400" />
              Activité récente
            </h3>
            <span className="text-gray-500 text-xs">temps réel</span>
          </div>
          {!stats?.activiteRecente || stats.activiteRecente.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">Aucune activité aujourd'hui</p>
          ) : (
            <div className="overflow-y-auto max-h-52">
              {stats.activiteRecente.map((op, i) => (
                <ActiviteItem key={i} op={op} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal clôture */}
      {modalCloture && (
        <ModalCloture
          stats={stats}
          onClose={() => setModalCloture(false)}
          onSuccess={() => { setModalCloture(false); charger(); }}
        />
      )}
    </div>
  );
}
